import { incidentCode } from "./incidentProfiles";
import { distanceKm } from "../providers/routing";
import {
  incidentLabels,
  requirements,
  fulfills,
  type SpeechRequest,
  type Incident,
  type RadioMessage,
  type SimState,
  type Vehicle,
} from "./model";

export const radioKindLabels: Record<RadioMessage["kind"], string> = {
  call: "Notrufeingang",
  alarm: "Alarmierung",
  status: "Statusmeldung",
  request: "Statusabfrage",
  speechRequest: "Sprechwunsch · Status 5",
  conversation: "Funkgespräch",
  situation: "Lagemeldung",
  warning: "Lageänderung",
  clear: "Einsatzabschluss",
};
export const tacticalStatus: Record<Vehicle["status"], string> = {
  1: "Einsatzbereit über Funk",
  2: "Einsatzbereit auf Wache",
  3: "Einsatzauftrag übernommen",
  4: "Am Einsatzort eingetroffen",
  6: "Nicht einsatzbereit",
  7: "Patient aufgenommen, Transport zum Krankenhaus",
  8: "Am Krankenhaus, Patientenübergabe läuft",
};
export const callsign = (v: Vehicle) => v.name.replace(/^SIM\s+/i, "");
export const controlCallsign = (s: SimState) =>
  `Leitstelle ${s.area.name.replace(/^(ILS|IRLS|Integrierte Leitstelle|Leitstelle)\s+/i, "").split(" · ")[0]}`;

export function emitRadio(
  s: SimState,
  input: Omit<RadioMessage, "id" | "sequence" | "at">,
) {
  const sequence = s.nextRadioSequence++;
  const message: RadioMessage = {
    ...input,
    id: `funk-${s.now}-${sequence}`,
    sequence,
    at: s.now,
  };
  s.radioMessages.push(message);
  s.radioMessages = s.radioMessages.slice(-300);
  const incident = s.incidents.find((i) => i.id === input.incidentId);
  if (
    incident &&
    (input.kind === "situation" ||
      input.kind === "warning" ||
      (input.status === 4 && incident.lastRadioAt === undefined))
  ) {
    incident.lastRadioAt = s.now;
    incident.nextRadioAt = s.now + (150 + radioRandom(incident) * 210) * 1000;
  }
  return message;
}
export function statusReport(s: SimState, v: Vehicle): string {
  const i = s.incidents.find((i) => i.id === v.incidentId);
  const j = v.journey;
  if (v.status === 6)
    return "Das Einsatzmittel ist nicht einsatzbereit. Keine Funkantwort simuliert.";
  let report = `Status ${v.status}. ${tacticalStatus[v.status]}.`;
  if (j?.routeStatus === "error")
    report +=
      " Wir stehen. Eine befahrbare Route konnte noch nicht berechnet werden.";
  else if (j?.routeStatus === "pending")
    report += " Wir warten auf die Routenberechnung.";
  else if (j?.routeStatus === "ready") {
    const minutes = Math.max(1, Math.ceil((j.end - s.now) / 60000));
    if (j.departureAt && s.now < j.departureAt)
      report += " Mannschaft stellt die Ausrückbereitschaft her.";
    else
      report +=
        v.status === 7
          ? " Transport nach " +
            (s.hospitals?.find((h) => h.id === v.transport?.hospitalId)?.name ??
              "Krankenhaus") +
            "."
          : j.returning
            ? " Auf Rückfahrt zur Wache."
            : " Auf Anfahrt zur Einsatzstelle.";
    report += ` Voraussichtliche ${j.returning ? "Rückkehr" : "Ankunft"} in ${minutes} ${minutes === 1 ? "Minute" : "Minuten"}.`;
  } else if (v.status === 4 && i) {
    report +=
      " Einsatz " +
      i.number +
      ", " +
      i.address +
      ". Besatzung arbeitet an der Einsatzstelle.";
  }
  return report;
}
function radioRandom(i: Incident) {
  // Separate persisted random stream: radio scheduling never changes incident generation.
  const seed = i.radioSeed ?? (i.number * 2654435761) >>> 0;
  i.radioSeed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
  return i.radioSeed / 4294967296;
}
function randomSituation(i: Incident) {
  const variants =
    !i.actions.length && i.workSeconds === 0
      ? [
          "Erkundung läuft. Weitere Rückmeldung folgt.",
          "Wir verschaffen uns einen Überblick. Rückmeldung nach der Erkundung.",
          "Noch keine neue Erkenntnis. Erkundung wird fortgesetzt.",
        ]
      : i.actions.length
        ? [
            `Maßnahmen laufen: ${i.actions.join(", ")}.`,
            `Wir arbeiten weiter an den gemeldeten Maßnahmen: ${i.actions.join(", ")}.`,
          ]
        : [
            "Die Einsatzmaßnahmen laufen. Weitere Rückmeldung folgt.",
            "Kräfte arbeiten an der Einsatzstelle. Noch keine Abschlussmeldung.",
          ];
  return variants[Math.floor(radioRandom(i) * variants.length)];
}
export function situationReport(i: Incident) {
  if (i.phase === "stabilisiert" || i.phase === "abgeschlossen")
    return i.type === "brand"
      ? "Feuer unter Kontrolle. Nachkontrolle abgeschlossen."
      : "Lage stabilisiert. Maßnahmen abgeschlossen.";
  if (!i.actions.length && i.workSeconds === 0)
    return i.reconnaissanceAt !== undefined
      ? "Erkundung abgeschlossen. Weitere Kräfte für die gemeldete Lage erforderlich."
      : "Erkundung läuft. Weitere Rückmeldung folgt.";
  if (i.actions.length)
    return `Laufende Maßnahmen: ${i.actions.join(", ")}. Einsatz noch nicht abgeschlossen.`;
  return "Erforderliche Kräfte sind vor Ort. Die Einsatzmaßnahmen laufen.";
}
function vehicleTransmission(s: SimState, v: Vehicle, text: string) {
  return {
    sender: callsign(v),
    text: `${controlCallsign(s)}, hier ${callsign(v)}. ${text} Kommen.`,
  };
}
export function requestStatus(s: SimState, vehicleId: string) {
  const v = s.vehicles.find((v) => v.id === vehicleId);
  if (!v) throw Error("Fahrzeug nicht gefunden.");
  const last = s.radioMessages.findLast(
    (m) => m.kind === "request" && m.vehicleId === vehicleId,
  );
  if (last && s.now - last.at < 10000)
    throw Error(
      "Status wurde gerade angefordert. Antwort im Funkprotokoll abhören; erneute Abfrage nach 10 Simulationssekunden.",
    );
  const control = controlCallsign(s);
  const transmissions = [
    {
      sender: control,
      text: `${callsign(v)}, hier ${control}. Geben Sie Ihren Standort und Status durch. Kommen.`,
    },
  ];
  if (v.status === 6)
    transmissions.push({
      sender: control,
      text: `${callsign(v)} ist als nicht einsatzbereit gemeldet. Keine Funkantwort. Ende.`,
    });
  else {
    const station = s.stations.find((w) => w.id === v.stationId);
    const nearest = [...s.stations].sort(
      (a, b) =>
        distanceKm(v.location, a.location) - distanceKm(v.location, b.location),
    )[0];
    const location =
      v.status === 2
        ? ` Standort ${station?.name ?? "Heimatwache"}.`
        : v.status === 4
          ? ""
          : nearest && distanceKm(v.location, nearest.location) < 2
            ? ` Position in der Nähe von ${nearest.name}.`
            : ` Position ${v.location[0].toFixed(4).replace(".", ",")} Grad Nord, ${v.location[1].toFixed(4).replace(".", ",")} Grad Ost.`;
    transmissions.push(
      vehicleTransmission(s, v, statusReport(s, v) + location),
    );
    transmissions.push({
      sender: control,
      text: `Hier ${control}. Verstanden. Ende.`,
    });
  }
  const message = emitRadio(s, {
    kind: "request",
    vehicleId,
    incidentId: v.incidentId ?? undefined,
    status: v.status,
    transmissions,
  });
  const incident = s.incidents.find((i) => i.id === v.incidentId);
  if (incident)
    incident.timeline.push({
      id: message.id,
      at: s.now,
      type: "status",
      text: `Statusabfrage ${callsign(v)}: ${statusReport(s, v)}`,
    });
}

export const speechReasonLabels: Record<SpeechRequest["reason"], string> = {
  reconnaissance: "Erste Rückmeldung",
  reinforcement: "Nachforderung / Lageänderung",
  update: "Lagemeldung",
  completion: "Abschlussmeldung",
};
const reasonPriority = {
  update: 0,
  reconnaissance: 1,
  reinforcement: 2,
  completion: 3,
};
export function requestSpeech(
  s: SimState,
  v: Vehicle,
  reason: SpeechRequest["reason"],
  urgent = false,
) {
  const i = s.incidents.find((i) => i.id === v.incidentId);
  if (!i || i.phase === "abgeschlossen" || v.status !== 4) return;
  const existing = s.speechRequests.find((r) => r.incidentId === i.id);
  if (existing) {
    if (existing.phase === "pending") {
      const becameUrgent = urgent && !existing.urgent;
      if (reasonPriority[reason] > reasonPriority[existing.reason])
        existing.reason = reason;
      existing.urgent ||= urgent;
      if (becameUrgent)
        emitRadio(s, {
          kind: "speechRequest",
          vehicleId: existing.vehicleId,
          incidentId: i.id,
          status: 5,
          transmissions: [
            {
              sender: callsign(v),
              text:
                controlCallsign(s) +
                ", hier " +
                callsign(v) +
                ". Dringender Sprechwunsch. Kommen.",
            },
          ],
        });
    }
    return;
  }
  const request: SpeechRequest = {
    id: "sprech-" + s.now + "-" + s.nextRadioSequence,
    vehicleId: v.id,
    incidentId: i.id,
    reason,
    urgent,
    createdAt: s.now,
    phase: "pending",
  };
  s.speechRequests.push(request);
  emitRadio(s, {
    kind: "speechRequest",
    vehicleId: v.id,
    incidentId: i.id,
    status: 5,
    transmissions: [
      {
        sender: callsign(v),
        text:
          controlCallsign(s) +
          ", hier " +
          callsign(v) +
          ". " +
          (urgent ? "Dringender Sprechwunsch." : "Sprechwunsch.") +
          " Kommen.",
      },
    ],
  });
  i.lastRadioAt = s.now;
  i.nextRadioAt = s.now + (150 + radioRandom(i) * 210) * 1000;
}
export function speechReport(
  s: SimState,
  v: Vehicle,
  reason: SpeechRequest["reason"],
) {
  const i = s.incidents.find((i) => i.id === v.incidentId)!;
  const deficits = Object.entries(requirements(i)).flatMap(([kind, count]) => {
    const assigned = s.vehicles.filter(
      (unit) =>
        unit.incidentId === i.id &&
        [3, 4].includes(unit.status) &&
        fulfills(unit.kind, kind as Vehicle["kind"], i),
    ).length;
    return assigned < count! ? [count! - assigned + " " + kind] : [];
  });
  const approach = s.vehicles.filter(
    (unit) => unit.incidentId === i.id && unit.status === 3,
  );
  let report = "Rückmeldung zu Einsatz " + i.number + ", " + i.address + ". ";
  report +=
    reason === "reconnaissance"
      ? "Erkundung abgeschlossen. " + i.title + ". "
      : "";
  report +=
    i.phase === "stabilisiert"
      ? situationReport(i)
      : !i.actions.length && !i.workSeconds
        ? i.reconnaissanceAt !== undefined
          ? "Erkundung abgeschlossen."
          : "Erkundung läuft."
        : reason === "update"
          ? randomSituation(i)
          : situationReport(i);
  if (
    reason === "reinforcement" &&
    i.type === "brand" &&
    i.escalation > 0 &&
    i.phase !== "stabilisiert"
  )
    report += " Der Brand hat sich ausgebreitet.";
  if (i.patients?.length)
    report +=
      " Patienten: " +
      i.patients.filter((p) => p.phase === "waiting").length +
      " vor Ort, " +
      i.patients.filter((p) => ["transport", "handover"].includes(p.phase))
        .length +
      " im Transport oder bei Übergabe, " +
      i.patients.filter((p) => p.phase === "delivered").length +
      " übergeben.";
  if (i.phase !== "stabilisiert") {
    if (deficits.length)
      report += " Benötigen zusätzlich " + deficits.join(", ") + ".";
    else if (approach.length)
      report +=
        " Weitere " +
        approach.length +
        " Einsatzmittel sind bereits auf Anfahrt.";
    else report += " Derzeit keine weiteren Kräfte erforderlich.";
  }
  return report;
}
export function grantSpeech(s: SimState, requestId: string) {
  const r = s.speechRequests.find((r) => r.id === requestId);
  if (!r || r.phase !== "pending")
    throw Error("Sprechwunsch nicht mehr offen. Funkübersicht aktualisieren.");
  if (s.speechRequests.some((item) => item.phase === "reported"))
    throw Error("Zuerst die laufende Rückmeldung mit „Verstanden“ bestätigen.");
  const v = s.vehicles.find(
    (v) =>
      v.id === r.vehicleId && v.incidentId === r.incidentId && v.status === 4,
  );
  if (!v)
    throw Error("Die Besatzung ist diesem Einsatz nicht mehr zugeordnet.");
  r.report = speechReport(s, v, r.reason);
  r.phase = "reported";
  r.reportedAt = s.now;
  const m = emitRadio(s, {
    kind: "conversation",
    vehicleId: v.id,
    incidentId: r.incidentId,
    transmissions: [
      {
        sender: controlCallsign(s),
        text: callsign(v) + ", hier " + controlCallsign(s) + ". Kommen.",
      },
      vehicleTransmission(s, v, r.report),
    ],
  });
  s.incidents
    .find((i) => i.id === r.incidentId)!
    .timeline.push({
      id: m.id,
      at: s.now,
      type: r.urgent ? "warning" : "status",
      text: callsign(v) + ": " + r.report,
    });
}
export function acknowledgeSpeech(s: SimState, requestId: string) {
  const r = s.speechRequests.find((r) => r.id === requestId);
  if (!r || r.phase !== "reported")
    throw Error("Zuerst den Sprechwunsch mit „Kommen“ freigeben.");
  const v = s.vehicles.find((v) => v.id === r.vehicleId)!;
  const i = s.incidents.find((i) => i.id === r.incidentId)!;
  if (r.reason === "completion" && i.phase === "stabilisiert")
    i.completionReportedAt = s.now;
  emitRadio(s, {
    kind: "conversation",
    vehicleId: v.id,
    incidentId: i.id,
    transmissions: [
      {
        sender: controlCallsign(s),
        text:
          callsign(v) + ", hier " + controlCallsign(s) + ". Verstanden. Ende.",
      },
    ],
  });
  s.speechRequests = s.speechRequests.filter((item) => item.id !== r.id);
  i.nextRadioAt = s.now + (150 + radioRandom(i) * 210) * 1000;
  if (i.pendingReinforcement) {
    delete i.pendingReinforcement;
    requestSpeech(s, v, "reinforcement", true);
  }
}

// Only factual transitions create traffic. On-scene reports wait for dispatcher permission.
export function recordRadioTransitions(before: SimState, s: SimState) {
  const oldUnits = new Map(before.vehicles.map((v) => [v.id, v]));
  const oldIncidents = new Map(before.incidents.map((i) => [i.id, i]));
  const control = controlCallsign(s);
  s.speechRequests = s.speechRequests.filter(
    (r) =>
      s.vehicles.some(
        (v) =>
          v.id === r.vehicleId &&
          v.incidentId === r.incidentId &&
          v.status === 4,
      ) &&
      s.incidents.some(
        (i) => i.id === r.incidentId && i.phase !== "abgeschlossen",
      ),
  );
  for (const i of s.incidents) {
    const old = oldIncidents.get(i.id);
    if (!old) {
      if (i.callId) continue;
      emitRadio(s, {
        kind: "call",
        incidentId: i.id,
        transmissions: [
          {
            sender: "Notruf 112",
            text:
              "Neuer Notruf. " +
              i.title +
              ". " +
              i.address +
              ", " +
              i.district +
              ". " +
              i.report.slice(0, 200),
          },
        ],
      });
      continue;
    }
    const alerted = s.vehicles.filter(
      (v) =>
        v.incidentId === i.id &&
        v.status === 3 &&
        oldUnits.get(v.id)?.incidentId !== i.id,
    );
    if (alerted.length)
      emitRadio(s, {
        kind: "alarm",
        incidentId: i.id,
        transmissions: [
          {
            sender: control,
            text:
              "Hier " +
              control +
              ". Einsatz für " +
              alerted.slice(0, 3).map(callsign).join(", ") +
              (alerted.length > 3
                ? " und " + (alerted.length - 3) + " weitere Einsatzmittel"
                : "") +
              ". " +
              incidentCode(i) +
              ", " +
              i.title +
              ". " +
              i.address +
              ", " +
              i.district +
              ". Einsatzauftrag übernehmen. Ende.",
          },
        ],
      });
    const onScene = s.vehicles.filter(
      (v) => v.incidentId === i.id && v.status === 4 && !v.journey,
    );
    const lead = onScene[0];
    if (i.escalation > old.escalation) {
      if (
        s.speechRequests.some(
          (r) => r.incidentId === i.id && r.phase === "reported",
        )
      )
        i.pendingReinforcement = true;
      if (lead) requestSpeech(s, lead, "reinforcement", true);
      else
        emitRadio(s, {
          kind: "warning",
          incidentId: i.id,
          transmissions: [
            {
              sender: control,
              text:
                "Hier " +
                control +
                ". Weitere Meldung zu Einsatz " +
                i.number +
                ". " +
                (i.timeline.findLast((e) => e.type === "warning")?.text ??
                  "Lageverschärfung. Kräftebedarf prüfen.") +
                " Ende.",
            },
          ],
        });
    }
    if (lead && i.phase !== "abgeschlossen") {
      if (i.phase === "stabilisiert" && i.completionReportedAt === undefined) {
        requestSpeech(s, lead, "completion");
      } else if (
        i.reconnaissanceAt !== undefined &&
        old.reconnaissanceAt === undefined
      ) {
        const missing = Object.entries(requirements(i)).some(
          ([kind, count]) =>
            s.vehicles.filter(
              (v) =>
                v.incidentId === i.id &&
                [3, 4].includes(v.status) &&
                fulfills(v.kind, kind as Vehicle["kind"], i),
            ).length < count!,
        );
        requestSpeech(
          s,
          lead,
          missing ? "reinforcement" : "reconnaissance",
          missing,
        );
      } else if (
        i.phase !== "stabilisiert" &&
        !s.speechRequests.some((r) => r.incidentId === i.id)
      ) {
        i.nextRadioAt ??= s.now + (150 + radioRandom(i) * 210) * 1000;
        if (s.now >= i.nextRadioAt || i.actions.length > old.actions.length) {
          const reporter = onScene[Math.floor(radioRandom(i) * onScene.length)];
          requestSpeech(s, reporter, "update");
        }
      }
    }
    if (i.phase === "abgeschlossen" && old.phase !== "abgeschlossen")
      emitRadio(s, {
        kind: "clear",
        incidentId: i.id,
        transmissions: [
          {
            sender: control,
            text:
              "Hier " +
              control +
              ". Einsatz " +
              i.number +
              ", " +
              i.address +
              ", abgeschlossen. Eingesetzte Kräfte, Rückfahrt antreten. Ende.",
          },
        ],
      });
  }
  for (const v of s.vehicles) {
    const old = oldUnits.get(v.id);
    if (!old || old.status === v.status) continue;
    // Digital arrival does not disclose a full situation report before Status 5/J.
    const report =
      v.status === 4
        ? "Status 4. Am Einsatzort eingetroffen."
        : statusReport(s, v);
    emitRadio(s, {
      kind: "status",
      vehicleId: v.id,
      incidentId: v.incidentId ?? old.incidentId ?? undefined,
      status: v.status,
      transmissions: [
        v.status === 6
          ? {
              sender: control,
              text: callsign(v) + ". Status 6. Nicht einsatzbereit.",
            }
          : vehicleTransmission(s, v, report),
      ],
    });
  }
}
